import type { AnalyticsProvider } from '~/plugins/firebase'

export function stripTags(data: string | null | undefined): string {
  const element: HTMLElement = document.createElement('div')

  element.innerHTML = data ?? ''

  return element.textContent ?? element.innerText ?? ''
}

export function isTest(): boolean {
  return globalThis.location?.href.includes('/deals/test/') ?? false
}

export function getAllowedOptionQuantities(option: Option): Array<number> {
  //@ts-ignore
  return [
    0,
    ...Array.from(
      option.allowedQuantities
        ? option.allowedQuantities
        : new Array(
            isTest()
              ? 999
              : option.stock !== null
                ? Math.max(0, isTest() ? 999 : option.stock + 1)
                : 999,
          ).keys(),
    ),
  ].filter((v, i, a) => a.indexOf(v) === i)
}

export function isApplePayAvailable(): boolean {
  //@ts-ignore
  return !!globalThis.ApplePaySession
}

export function getUrl(routePath: string, ignoreApp: boolean = false): string {
  const origin =
    !ignoreApp && isApp() ? 'outspot://' : globalThis.location.origin
  return origin + routePath
}

export function getRouteUrl(
  routePath: string,
  ignoreApp: boolean = false,
): string {
  return getUrl(getRoute(routePath), ignoreApp)
}

export function getRoute(routePath: string): string {
  const currentLocale = useCurrentLocale()
  return `/${currentLocale}/${routePath}`
}

export function getDealRoute(deal?: Deal, routePath?: string): string {
  if (!deal) {
    return getRoute('')
  }

  const routePathPrefix = isTest() ? 'deals/test' : 'deals'

  routePath = routePath
    ? `${routePathPrefix}/${deal.slug}/${routePath}`
    : `${routePathPrefix}/${deal.slug}/`

  return getRoute(routePath)
}

export function getDealRouteUrl(deal?: Deal, routePath?: string): string {
  return getUrl(getDealRoute(deal, routePath))
}

export function enableTreeBuyOptions(): boolean {
  return true
  // const locale = useCurrentLocale()
  // const activeList = ['nl', 'fr', 'ie', 'sv']
  // if (activeList.includes(locale)) {
  //   return true
  // }
  // return false
}

export function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function pushToDataLayer(data: any): void {
  try {
    if (shouldDisableTracking()) {
      return
    }

    //@ts-ignore
    globalThis.dataLayer = globalThis.dataLayer || []

    // @ts-ignore
    globalThis.dataLayer.push(data)
  } catch (e) {
    console.error(e)
  }
}

export function pushCustomEventToDataLayer(event: string, data?: any): void {
  try {
    pushToDataLayer({
      event: event,
      ...(data ?? {}),
    })
  } catch (e) {
    console.error(e)
  }
}

export function pushEcommerceEventToDataLayer(
  event: string,
  value: number,
  data?: any,
): void {
  if (shouldDisableTracking()) {
    return
  }

  try {
    const authentication = useAuthentication()
    const user = authentication.getUser()

    if (isApp()) {
      const analytics = useNuxtApp().$firebaseAnalytics as
        | AnalyticsProvider
        | undefined
      if (analytics?.logEvent) {
        const ecommerce = {
          currency: useGlobalMessage('language.currency'),
          value: value,
          ...(data ?? {}),
        }

        analytics.logEvent(event, { ...ecommerce })
      } else {
        console.warn(
          'Firebase Analytics is not available or something went wrong',
        )
      }
    } else {
      const ecommerce = {
        currency: useGlobalMessage('language.currency'),
        value: value,
        user_properties: {
          first_name: user?.firstName,
          last_name: user?.lastName,
          email: user?.email,
          phone: user?.phone,
          zip: undefined,
          country: undefined,
          region: undefined,
          address: undefined,
          city: undefined,
        },
        ...(data ?? {}),
      }

      pushCustomEventToDataLayer(event, { ecommerce })
    }
  } catch (e) {
    console.error(e)
  }
}

export function parseDate(date: string | null | Date): Date {
  if (
    typeof date === 'string' &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)
  ) {
    // Safari likes T instead of space
    date = date.replaceAll('-', '/')
    return new Date(date)
  } else if (typeof date === 'string') {
    return new Date(date)
  }

  if (date instanceof Date) {
    return new Date(date)
  }

  return new Date()
}

export function timeout(delay: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delay))
}

export function formatPreciseDate(dateObj: string, showTime: boolean): string {
  const now = parseDate(dateObj)
  const i18n = useI18n()
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(showTime && { hour: 'numeric', minute: 'numeric' }),
  }
  return now.toLocaleString(i18n.t('language.locale'), options)
}

export function isRecurringPaymentMethodIdentifier(
  paymentMethodIdentifier: string,
): boolean {
  return ['creditcard', 'creditcard-hosted'].includes(paymentMethodIdentifier)
}

export function isOutspotPlusCompatiblePaymentMethodIdentifier(
  paymentMethodIdentifier: string,
): boolean {
  return ['bancontact', 'creditcard', 'creditcard-hosted'].includes(
    paymentMethodIdentifier,
  )
}

export function isRegionUS() {
  return useGlobalMessage('language.region') === 'us'
}

export function isRegionEU() {
  return useGlobalMessage('language.region') === 'eu'
}

export function isLocale(code: string) {
  return useCurrentLocale() === code
}

export function isValidDutchPostalCode(postalCode: string): boolean {
  if (!isLocale('nd')) return true
  const dutchPostalCodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{2}$/i // Matches '1234 AB', '1234ab', '1234 ab', '1234AB', etc.
  return dutchPostalCodeRegex.test(postalCode)
}

export function hasGoogleReviews() {
  // return true because disable trustpilot,
  // to enable trustpilot again use isLocale() function
  // for countries where only google will be shown
  return true
}

export function hasTrustpilotReviews() {
  return !hasGoogleReviews()
}

export function hasInteractiveReviews() {
  return !isLocale('us')
}

export function makeNumberFormat(currency: string): Record<string, unknown> {
  return {
    currencyWithSymbol: {
      style: 'currency',
      currency: currency,
      notation: 'standard',
    },
    currencyWithoutSymbol: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  }
}

export function makeDateTimeFormat(): Record<string, unknown> {
  return {
    short: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
  }
}

export function clearSessionAddresses() {
  const session = useSessionStorage()

  for (let id in session.orders) {
    session.orders[id].deliveryAddress = {}
    session.orders[id].invoiceAddress = {}
  }
}
export function formatCurrency(value) {
  const i18n = useI18n()
  const locale = i18n.t('language.locale')
  const format = makeNumberFormat(i18n.t('language.currency'))
  return i18n.n(parseFloat(value), format.currencyWithoutSymbol, locale)
}

export function shouldShowMyTravels(): boolean {
  return (
    isLocale('nl') ||
    isLocale('fr') ||
    isLocale('nd') ||
    isLocale('fra') ||
    isLocale('it') ||
    isLocale('de')
  )
}

export function shouldEnableServicePoints(): boolean {
  return isLocale('nl') || isLocale('fr') || isLocale('nd') || isLocale('fra')
}

export function shouldDisableTracking(): boolean {
  return false
}

export function storageVersion(): number | undefined {
  // If we make big changes in the storage structure, increase the number here to reset the users storage, for now we only test with it
  if (
    globalThis.location?.pathname.includes('/it/') ||
    globalThis.location?.pathname.includes('/pl/')
  ) {
    return 7
  }

  return undefined
}

const isWebPSupported = (() => {
  try {
    return (
      document
        .createElement('canvas')
        .toDataURL('image/webp')
        .indexOf('data:image/webp') === 0
    )
  } catch (e) {
    return false
  }
})()

export function convertJpgToWebPIfSupported(
  imagePath: string,
  force: boolean = false,
): string {
  if (force || isWebPSupported) {
    return imagePath.replace(/\.jpg$/, '.webp')
  }

  return imagePath
}

export function isSoldOut(deal?: Deal): boolean {
  if (isTest()) {
    return false
  }

  const optionsWithStock: Option[] = []

  deal?.variants?.forEach((variant: Variant) => {
    variant.options?.forEach((option: Option) => {
      if (option.stock !== 0) {
        optionsWithStock.push(option)
      }
    })
  })

  return optionsWithStock.length === 0
}

export function enableGeoPostcodes(): boolean {
  return (
    isLocale('nl') ||
    isLocale('fr') ||
    isLocale('fra') ||
    isLocale('de') ||
    isLocale('nd')
  )
}

export function shouldReverseStreetAndNumber(): boolean {
  return isLocale('fra') || isLocale('nd')
}

export function getCountryCodeByLocale(locale: string): string {
  const countryCodeMap: Record<string, string> = {
    nl: '+32',
    fr: '+32',
    nd: '+31',
    de: '+49',
    en: '+44',
    es: '+34',
    ie: '+353',
    fra: '+33',
    it: '+39',
    at: '+43',
    pl: '+48',
    pt: '+351',
    sv: '+46',
    fi: '+358',
  }

  return countryCodeMap[locale]
}

export function shouldShowVerticalDeals(): boolean {
  const authentication = useAuthentication()
  const user = authentication.getUser()

  if (!user.idh) {
    return false
  }

  if (['1', '2', '3'].includes(user.idh?.charAt(0))) {
    return true
  }

  return false
}
